import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Link, Redirect } from 'react-router-dom';
import moment from 'moment';

import styles from './Home.module.css';

import imgLogo from '../components/navbar/logo.png';

import { AppContext } from '../services/AppContext';
import { Loading } from '../components/loading/Loading';
import { SearchNavbar } from '../components/navbar/Navbar';
import Sidebar from '../components/sidebar/Sidebar';
import EmptyState from '../components/empty/EmptyState';
import BottomBar from '../components/bottombar/BottomBar';
import { Footer } from '../components/Footer';
import { LoginModal } from './LoginModal';
import { HelpButton } from './help/Help';

/*

380 - Safe Patient Handling
381 - Privacy
382 - Media Release Guide
383 - Workplace Violence
384 - Survey
385 - EMTALA
493 - Consent
458 - Compliance
459 - 'Discharge Planning 2019'
472 - Consent Requirements for Medical Treatment of Adults
471 - Assault and Abuse Reporting Requirement
469 - Patients Rights Posters
473 - Consent Requirements for Medical Treatment of Minors
474 - Productive Parts
475 - Record Retention

Risk Manager and Privacy
1. Consent (493)
2. Privacy (381)
3. Survey (384)
4. EMTALA (385)
5. Compliance (458)
6. Discharge Planning for Homeless Patients (459)
7. Healthcare Workplace Violence Prevention (383)

Quick Reference Guides
1. Consent Requirements for Medical Treatment of Adults (472)
2. Assault and Abuse Reporting Requirement (471)
3. Consent Requirements for Medical Treatment of Minors (473)
4. Patients Rights Posters (469)
5. Discharge Planning for Homeless Patients (459)

View All Collections
1. Compliance (458)
2. Record Retention (475)
3. Discharge Planning for Homeless Patients (459)
4. Productive Partners (474)
5. EMTALA (385)
6. Consent (493)
7. Survey (384)
8. Privacy (381)
9. Consent (386) // Moved to archive
10. Healthcare Workplace Violence Prevention (383)
11. Consent Requirements for Medical Treatment of Minors (473)
12. Consent Requirements for Medical Treatment of Adults (472)
13. Assault and Abuse Reporting Requirement (471)
14. Patients Rights Posters (469)

*/

const ISSUES_SET = {
  risk: [
    '2035',  // Compliance 2026
		'955', // Consent 2024
		'381',
		'384',
		'385',
		'629',
		'459',
		'383',
	],
	quick_reference: ['472', '471', '473', '469', '459'],
  all: [
	  '2035',  // Compliance 2026
		'2034',
    '629',
		'475',
		'459',
		'474',
		'385',
		'955', // Consent 2024
		'384',
		'381',
		'383',
		'473',
		'472',
		'471',
		'469',
	],
};

export default class Home extends React.Component {
	static contextType = AppContext;
	state = {
		issues: [],
		isLoading: true,
		isSubscribed: false,
		displayInstallationOption: false,
		redirect: null,
		isStandalone: false,
	};

	componentDidMount() {
		this.onLoad()
			.then(() => {
				console.log(`Home view loaded`);
			})
			.catch(console.error);
	}

	onLoad = async () => {
		const { location } = this.props;
		localStorage.setItem('last-path', location.pathname);

		const { tracking, issueMode } = this.context;
		if (tracking) {
			tracking.pageView({
				location: window.location.pathname,
			});
		}

		this.displayInstallation();
		this.setState({ isLoading: true });
		try {
			const issues = await this.getData();
			if (issueMode?.enabled && issues.length && issues[0].id) {
				console.debug(`Issue mode enabled`);
				const issue = issues[0];
				const { slug } = issue;
				this.setState({
					redirect: (
						<Redirect to={`/issues/${slug ? slug : issue.id}`} />
					),
				});
				return;
			}

			this.setState({ isLoading: false, issues });
		} catch (e) {
			console.error(e);
			this.setState({ isLoading: false, error: e.message });
		}
	};

	getData = async () => {
		const { isUserAdmin } = this.context;
		if (!isUserAdmin) {
			const isSubscribed = await this.hasSubscription();
			this.setState({ isSubscribed });
		} else {
			this.setState({ isSubscribed: true });
		}

		const issues = await this.loadIssues();

		return issues;
	};

	displayInstallation = () => {
		if (this.isIOSDevice()) {
			// @ts-ignore
			if (!window.navigator?.standalone) {
				if (!localStorage.getItem('installation-requested')) {
					localStorage.setItem('installation-requested', 'true');
					this.setState({
						dragEnabled: false,
						displayInstallationOption: true,
					});
				}
			}
		}
	};

	isIOSDevice = () => {
		// @ts-ignore
		return window.navigator.standalone !== undefined;
	};

	onCloseInstallation = () => {
		this.getData()
			.then((issues) => {
				this.setState({
					isLoading: false,
					issues,
					dragEnabled: true,
					displayInstallationOption: false,
				});
			})
			.catch((e) => {
				this.setState({
					isLoading: false,
					error: e.message,
					dragEnabled: true,
					displayInstallationOption: false,
				});
			});
	};

	loadIssues = async () => {
		// stripeKey
		const { api, isOnline, db, StripeAccountID, isUserAdmin } =
			this.context;
		let issues;

		const downloadedIssues = JSON.parse(
			localStorage.getItem('downloaded-issues')
		);

		if (isOnline) {
			console.debug('Fetching issues from remote');
			let remoteIssues = await api.getIssues();
			if (!StripeAccountID && !isUserAdmin) {
				remoteIssues = remoteIssues.filter((issue) => {
					const { isPaid, price } = issue;
					if (isPaid && price) {
						return false;
					}

					return true;
				});
			}

			let retryCount = 3;
			while (remoteIssues.length === 0 && !!retryCount) {
				await this.timeout();
				remoteIssues = await api.getIssues();
				retryCount--;
			}

			issues = remoteIssues.map((issue) => {
				issue.isDownloaded = !!downloadedIssues[`${issue.id}`];
				return issue;
			});

			await db.saveIssues(issues);
			return issues;
		}

		console.debug('Fetching issues from cache');
		issues = await db.getIssues();

		issues = issues.map((issue) => {
			issue.isDownloaded = !!downloadedIssues[`${issue.id}`];
			return issue;
		});

		return issues;
	};

	timeout = async (ms = 500) => {
		return new Promise((resolve) => {
			setTimeout(resolve, ms);
		});
	};

	hasSubscription = async () => {
		const { api, isOnline } = this.context;
		if (isOnline) {
			const response = await api.getMySubscription();
			if (!!response && !!response.expiredAt) {
				const { expiredAt } = response;
				localStorage.setItem('subscription_expired_at', expiredAt);
				if (moment(new Date()).isBefore(new Date(expiredAt), 'day')) {
					return true;
				}
			}
			return false;
		}

		const subscriptionExpiredDate = localStorage.getItem(
			'subscription_expired_at'
		);

		if (!subscriptionExpiredDate) return false;

		return moment(
			// @ts-ignore
			new Date().isBefore(new Date(subscriptionExpiredDate), 'day')
		);
	};

	render() {
		let { issues, isLoading, redirect } = this.state;

		if (redirect) {
			return redirect;
		}

		if (isLoading || !issues) {
			return <LoadingComponent />;
		}

		issues = issues.sort((a, b) => a.index - b.index);

		return <ChaLayout issues={issues} />;
	}
}

Home.propTypes = {
	history: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
};

function ChaLayout({ issues }) {
	const [isLoginOpen, setIsLoginOpen] = useState(false);
	const issuesMap = useMemo(() => {
		const response = new Map();
		if (!issues?.length) {
			return response;
		}
		for (const issue of issues) {
			response.set(issue.id, issue);
		}
		return response;
	}, [issues]);
	if (!issues.length) {
		return null;
	}

	return (
		<>
			<LoginModal
				isOpen={isLoginOpen}
				onClose={() => {
					setIsLoginOpen(false);
				}}
			/>
			<div className={styles['cha']}>
				<Navbar setIsLoginOpen={setIsLoginOpen} />
				<main className={styles['collections']}>
					<Toolbar />
					<Collection
						setIsLoginOpen={setIsLoginOpen}
						issues={[...ISSUES_SET['risk']].map((id) =>
							issuesMap.get(id)
						)}
						title="Risk Manager and Privacy"
					/>
					<Collection
						setIsLoginOpen={setIsLoginOpen}
						issues={[...ISSUES_SET['quick_reference']].map((id) =>
							issuesMap.get(id)
						)}
						title="Quick Reference Guides"
					/>
					<Collection
						setIsLoginOpen={setIsLoginOpen}
						issues={[...ISSUES_SET['all']].map((id) =>
							issuesMap.get(id)
						)}
						title="View All Collections"
					/>
					<Footer />
				</main>
			</div>
		</>
	);
}

export function Navbar({ setIsLoginOpen }) {
	return (
		<nav>
			<div>
				<Link to={`/`}>
					<img src={imgLogo} />
				</Link>
			</div>
			<div>
				<HelpButton />
				<LoginButton setIsLoginOpen={setIsLoginOpen} />
			</div>
		</nav>
	);
}

Navbar.propTypes = {
	setIsLoginOpen: PropTypes.any,
};

/*function filterByCollection(collection) {
	return (issue) => {
		return ISSUES_SET[collection]
			? ISSUES_SET[collection].has(issue.id)
			: false;
	};
}*/

ChaLayout.propTypes = {
	issues: PropTypes.array.isRequired,
};

function LoginButton({ setIsLoginOpen }) {
	const { setUserLoggedIn, isLogged } = useContext(AppContext);
	if (isLogged) {
		return (
			<button
				onClick={() => {
					localStorage.removeItem('is-user-login');
					setUserLoggedIn(false);
				}}
			>
				Log out
			</button>
		);
	}
	return (
		<button
			onClick={() => {
				setIsLoginOpen(true);
			}}
		>
			Login
		</button>
	);
}

LoginButton.propTypes = {
	setIsLoginOpen: PropTypes.any,
};

function Collection({ issues, title, setIsLoginOpen }) {
	const { isLogged } = useContext(AppContext);
	console.log(`TITLE: "${title}"\n`, issues);
	console.log(`IS USER LOGGED IN: ${isLogged}`);
	if (!isLogged) {
		return (
			<section
				className={styles['collection']}
				onClick={() => {
					setIsLoginOpen(true);
				}}
			>
				<h3>{title}</h3>
				<div className={styles['issues']}>
					{issues.map((data) => {
						if (!data) {
							return null;
						}
						const { id, cover } = data;
						return (
							<span className={styles['issue']} key={id}>
								<img src={cover} width={1024} />
							</span>
						);
					})}
				</div>
			</section>
		);
	}
	return (
		<section className={styles['collection']}>
			<h3>{title}</h3>
			<div className={styles['issues']}>
				{issues.map((data) => {
					if (!data) {
						return null;
					}
					const { id, slug, cover } = data;
					return (
						<Link
							className={styles['issue']}
							key={id}
							to={`/issues/${slug ? slug : id}`}
						>
							<img width={1024} src={cover} />
						</Link>
					);
				})}
			</div>
		</section>
	);
}

Collection.propTypes = {
	title: PropTypes.string.isRequired,
	issues: PropTypes.array.isRequired,
	setIsLoginOpen: PropTypes.func.isRequired,
};

function LoadingComponent() {
	const { config } = useContext(AppContext);
	return (
		<div className={[styles['cha'], styles['loading']].join(' ')}>
			<main className={styles['loading']}>
				<Loading theme={config?.theme?.loading} />
			</main>
		</div>
	);
}

export function Toolbar() {
	return (
		<div className={styles['toolbar']}>
			<div>
				<a
					href="https://calhospital.org/events/"
					rel="noreferrer"
					target={'_blank'}
				>
					Upcoming Programs
				</a>
				<a
					href="https://calhospital.org/education-publications/cha-on-demand-learning/#on-demand-recordings"
					rel="noreferrer"
					target={'_blank'}
				>
					On-Demand Learning
				</a>
				<a
					href="https://calhospital.org/publications/forms-and-appendices/"
					rel="noreferrer"
					target={'_blank'}
				>
					Sample Forms
				</a>
				<Link to="/archive">Archive</Link>
			</div>
		</div>
	);
}

function Empty({ onLoad }) {
	const { isOnline, theme } = useContext(AppContext);
	return (
		<div className="App">
			<SearchNavbar
				onLogoClick={() => {
					onLoad()
						.then(() => {})
						.catch(console.error);
				}}
				isOnline={isOnline}
				theme={theme.navbar}
			/>
			<main>
				<section className={styles['home']}>
					<Sidebar theme={theme.bottomBar} section="home" />
					<EmptyState section={'publication'} />
				</section>
			</main>
			<BottomBar
				section="home"
				isVisible={true}
				theme={theme.bottomBar}
			/>
		</div>
	);
}

Empty.propTypes = {
	onLoad: PropTypes.func,
};
